import { Award, Calendar, Hash, BadgeCheck } from "lucide-react";

interface CertificateGeneratorProps {
  learnerName: string;
  blogPostTitle: string;
  completionDate: string;
  certificateNumber: string;
}

export const CertificateGenerator = ({
  learnerName,
  blogPostTitle,
  completionDate,
  certificateNumber,
}: CertificateGeneratorProps) => {
  const goldColor = "#d4af37";
  const navyColor = "#1a365d";
  const creamColor = "#faf8f3";
  const mutedColor = "#6b7280";

  return (
    <div
      id="certificate-content"
      className="w-[800px] h-[600px] flex flex-col items-center justify-center relative"
      style={{
        fontFamily: "'Georgia', 'Times New Roman', serif",
        background: `linear-gradient(135deg, ${creamColor} 0%, #f5f1e8 100%)`,
        padding: "48px",
        wordSpacing: "4px",
        letterSpacing: "0.5px",
      }}
    >
      {/* Elegant Gold Border */}
      <div
        className="absolute inset-4"
        style={{
          border: `4px double ${goldColor}`,
          borderRadius: "4px",
        }}
      />
      <div
        className="absolute inset-6"
        style={{
          border: `1px solid ${goldColor}`,
          borderRadius: "2px",
        }}
      />

      {/* Decorative corners */}
      <div
        className="absolute top-12 left-12 w-16 h-16"
        style={{
          borderTop: `3px solid ${goldColor}`,
          borderLeft: `3px solid ${goldColor}`,
        }}
      />
      <div
        className="absolute top-12 right-12 w-16 h-16"
        style={{
          borderTop: `3px solid ${goldColor}`,
          borderRight: `3px solid ${goldColor}`,
        }}
      />
      <div
        className="absolute bottom-12 left-12 w-16 h-16"
        style={{
          borderBottom: `3px solid ${goldColor}`,
          borderLeft: `3px solid ${goldColor}`,
        }}
      />
      <div
        className="absolute bottom-12 right-12 w-16 h-16"
        style={{
          borderBottom: `3px solid ${goldColor}`,
          borderRight: `3px solid ${goldColor}`,
        }}
      />

      {/* Award Icon */}
      <div className="mb-4">
        <Award className="h-16 w-16" style={{ color: goldColor }} />
      </div>

      {/* Decorative line */}
      <div
        className="w-32 h-[2px] mb-4"
        style={{ backgroundColor: goldColor }}
      />

      {/* Title */}
      <h1
        className="text-5xl font-bold mb-1 tracking-wide"
        style={{
          color: navyColor,
          letterSpacing: "4px",
          wordSpacing: "8px",
        }}
      >
        CERTIFICATE
      </h1>
      <h2
        className="text-2xl mb-6"
        style={{
          color: mutedColor,
          letterSpacing: "3px",
          wordSpacing: "6px",
        }}
      >
        OF COMPLETION
      </h2>

      {/* Decorative line */}
      <div
        className="w-32 h-[2px] mb-6"
        style={{ backgroundColor: goldColor }}
      />

      {/* Body Text */}
      <p
        className="text-base mb-3"
        style={{
          color: mutedColor,
          letterSpacing: "1px",
          wordSpacing: "4px",
        }}
      >
        This is to certify that
      </p>

      <p
        className="text-4xl font-bold mb-5 px-8 pb-2"
        style={{
          color: navyColor,
          borderBottom: `2px solid ${goldColor}`,
          letterSpacing: "2px",
          wordSpacing: "8px",
        }}
      >
        {learnerName}
      </p>

      <p
        className="text-base mb-3"
        style={{
          color: mutedColor,
          letterSpacing: "1px",
          wordSpacing: "4px",
        }}
      >
        has successfully completed the
      </p>

      <p
        className="text-2xl font-semibold mb-2 text-center max-w-[600px]"
        style={{
          color: navyColor,
          letterSpacing: "1px",
          wordSpacing: "4px",
        }}
      >
        28-Day Learning Roadmap
      </p>

      <p
        className="text-xl mb-6 text-center max-w-[600px] font-medium"
        style={{
          color: goldColor,
          letterSpacing: "0.5px",
          wordSpacing: "4px",
        }}
      >
        "{blogPostTitle}"
      </p>

      {/* Footer Details */}
      <div
        className="flex items-center gap-8 text-sm mt-auto"
        style={{
          color: mutedColor,
          letterSpacing: "0.5px",
          wordSpacing: "3px",
        }}
      >
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4" style={{ color: goldColor }} />
          <span>Completed: {completionDate}</span>
        </div>
        <div className="flex items-center gap-2">
          <Hash className="h-4 w-4" style={{ color: goldColor }} />
          <span>Certificate: {certificateNumber}</span>
        </div>
      </div>

      {/* Signature Line */}
      <div className="mt-4 text-center">
        <div
          className="w-48 h-[1px] mx-auto mb-1"
          style={{ backgroundColor: navyColor }}
        />
        <p
          className="text-xs"
          style={{
            color: mutedColor,
            letterSpacing: "1px",
            wordSpacing: "3px",
          }}
        >
          Authorized by RunoBlog
        </p>
      </div>

      {/* Brand */}
      <div className="mt-3 text-center">
        <p
          className="text-2xl font-bold"
          style={{
            color: goldColor,
            letterSpacing: "2px",
            wordSpacing: "4px",
          }}
        >
          RunoBlog
        </p>
        <p
          className="text-xs"
          style={{
            color: mutedColor,
            letterSpacing: "1px",
            wordSpacing: "3px",
          }}
        >
          www.runoblog.site
        </p>
      </div>

      {/* Decorative Seal */}
      <div
        className="absolute bottom-16 left-16"
        style={{
          width: "64px",
          height: "64px",
          borderRadius: "50%",
          border: `3px solid ${goldColor}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: creamColor,
        }}
      >
        <BadgeCheck style={{ color: goldColor, width: "36px", height: "36px" }} />
      </div>
    </div>
  );
};
